import { useEffect, useState } from 'react';
import { useLoaderData, useRouteLoaderData } from 'react-router';
import { fetchProductOfferByOfferId } from '../fetch.js';
import type { InstallmentOption } from '../../generated/api/installmentOption.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { PublicDeviceOfferLoaderData, PublicPageOrDeviceLoaderData } from '../loaders.js';
import type { Dispatch, SetStateAction } from 'react';

interface UseDeviceOfferDataParams {
  offer?: ProductOfferingsResponse;
  allOffers?: ProductOfferingsResponse[];
}

interface UseDeviceOfferDataResult {
  deviceOffer?: ProductOfferingsResponse;
  allDeviceOffers: ProductOfferingsResponse[];
  refreshError: Error | null;
  isRefreshing: boolean;
}

export const useDeviceOfferData = ({ offer, allOffers }: UseDeviceOfferDataParams): UseDeviceOfferDataResult => {
  const routeLoaderData = useLoaderData() as PublicDeviceOfferLoaderData | undefined;
  const rootLoaderData = useRouteLoaderData('public-path-root') as PublicPageOrDeviceLoaderData | undefined;
  const initialDeviceData = routeLoaderData ?? rootLoaderData?.deviceOfferData;
  const [deviceData, setDeviceData] = useState<PublicDeviceOfferLoaderData | undefined>(initialDeviceData);
  const [refreshError, setRefreshError] = useState<Error | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(!offer);

  const deviceOffer = offer ?? deviceData?.offer;
  const allDeviceOffers = allOffers ?? deviceData?.allOffers ?? [];
  const offerId = deviceOffer?.id;

  useEffect(() => {
    if (offer || !offerId) {
      setIsRefreshing(false);
      return;
    }

    let isCancelled = false;
    setRefreshError(null);
    setIsRefreshing(true);

    const loadOffer = async () => {
      try {
        const productOffer = await fetchProductOfferByOfferId(offerId);
        if (isCancelled) {
          return;
        }

        setDeviceData(prev => ({
          offer: productOffer,
          allOffers: allOffers ?? prev?.allOffers ?? [productOffer],
          slug: prev?.slug ?? routeLoaderData?.slug ?? '',
        }));
      } catch (error) {
        if (isCancelled) {
          return;
        }
        setRefreshError(error instanceof Error ? error : new Error('Failed to fetch product offer'));
      } finally {
        if (!isCancelled) {
          setIsRefreshing(false);
        }
      }
    };

    void loadOffer();

    return () => {
      isCancelled = true;
    };
  }, [offer, allOffers, routeLoaderData?.slug, offerId]);

  return { deviceOffer, allDeviceOffers, refreshError, isRefreshing };
};

export const useSelectedInstallmentOption = (
  installmentOptions: InstallmentOption[]
): [string, Dispatch<SetStateAction<string>>] => {
  const [selectedInstallmentId, setSelectedInstallmentId] = useState('');

  useEffect(() => {
    const defaultId = installmentOptions[0] ? String(installmentOptions[0].numberOfInstallment) : '';
    if (!installmentOptions.length) {
      setSelectedInstallmentId('');
      return;
    }

    const hasSelection = installmentOptions.some(
      option => String(option.numberOfInstallment) === selectedInstallmentId
    );

    if (!hasSelection) {
      setSelectedInstallmentId(defaultId);
    }
  }, [installmentOptions, selectedInstallmentId]);

  return [selectedInstallmentId, setSelectedInstallmentId];
};
